import {tx, ptx, usePageText} from '../lib/page-text';
import { getIceServers } from '../lib/ice';
import { useState } from 'react';

interface Result {
  rtt: { url: string; ms: number; status: 'ok' | 'fail' }[];
  download: { mbps: number; durationMs: number } | null;
  upload: { mbps: number; durationMs: number } | null;
  codecs: { codec: string; supported: boolean }[];
  webrtc: { supported: boolean; ipv4: string | null; ipv6: string | null; natType: string | null };
}

const RTT_TARGETS = [
  { label: 'Kardolive API (us-east-1)', url: '/v1/health' },
  { label: 'CloudFront edge', url: '/status/' },
  { label: 'Generic 1.1.1.1', url: 'https://1.1.1.1/cdn-cgi/trace' },
];

const CODECS_TO_TEST = [
  'video/mp4; codecs="avc1.42E01E"',         // H.264 baseline
  'video/mp4; codecs="hev1.1.6.L93.B0"',     // H.265
  'video/webm; codecs="vp09.00.10.08"',      // VP9
  'video/mp4; codecs="av01.0.04M.08"',       // AV1
  'audio/mp4; codecs="mp4a.40.2"',           // AAC
  'audio/webm; codecs="opus"',               // Opus
];

async function probe(url: string, timeoutMs = 5000): Promise<{ ms: number; ok: boolean }> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  const start = performance.now();
  try {
    const r = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: ctrl.signal });
    return { ms: Math.round(performance.now() - start), ok: r.ok || r.status === 405 };
  } catch {
    return { ms: 0, ok: false };
  } finally { clearTimeout(t); }
}

async function measureDownload(): Promise<{ mbps: number; durationMs: number }> {
  // Download a known segment of our CDN test file. We use a 1MB region of one
  // of the static asset files served by CloudFront — won't hit the origin if
  // the asset is cached at the edge (which is the realistic scenario).
  const url = '/admin/assets/index-Cd5rl98T.js';
  const start = performance.now();
  try {
    const r = await fetch(url, { cache: 'no-store' });
    const buf = await r.arrayBuffer();
    const ms = performance.now() - start;
    const mbps = (buf.byteLength * 8) / 1024 / 1024 / (ms / 1000);
    return { mbps: Number(mbps.toFixed(2)), durationMs: Math.round(ms) };
  } catch {
    return { mbps: 0, durationMs: 0 };
  }
}

async function measureUpload(): Promise<{ mbps: number; durationMs: number }> {
  // Upload 1 MB of random data to the API health endpoint via POST with body.
  // The body is discarded — we just measure how fast we can push it.
  const data = new Uint8Array(1024 * 1024);
  crypto.getRandomValues(data);
  const start = performance.now();
  try {
    await fetch('/v1/health', { method: 'POST', body: data, cache: 'no-store' });
    const ms = performance.now() - start;
    const mbps = (data.length * 8) / 1024 / 1024 / (ms / 1000);
    return { mbps: Number(mbps.toFixed(2)), durationMs: Math.round(ms) };
  } catch {
    return { mbps: 0, durationMs: 0 };
  }
}

async function discoverWebRtc(ice: RTCIceServer[]): Promise<Result['webrtc']> {
  if (!('RTCPeerConnection' in window)) {
    return { supported: false, ipv4: null, ipv6: null, natType: null };
  }
  return new Promise((resolve) => {
    const pc = new RTCPeerConnection({ iceServers: ice });
    let ipv4: string | null = null;
    let ipv6: string | null = null;
    let srflx = false;
    // A relay candidate proves the TURN path works from this network — the one
    // thing that decides whether a viewer behind a strict firewall can watch at
    // WebRTC latency or gets dropped to the 10-second ladder.
    let relay = false;
    pc.onicecandidate = (evt) => {
      if (!evt.candidate) {
        pc.close();
        const natType = !srflx ? 'unknown (no STUN reply)' : 'srflx (likely behind NAT)';
        resolve({ supported: true, ipv4, ipv6, natType: relay ? `${natType} · TURN relay reachable` : natType });
        return;
      }
      const c = evt.candidate.candidate;
      const m = c.match(/(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/);
      if (m) ipv4 = m[1];
      const m6 = c.match(/([a-fA-F0-9:]+:+[a-fA-F0-9]+)/);
      if (m6 && c.includes('srflx')) ipv6 = m6[1];
      if (c.includes('srflx')) srflx = true;
      if (c.includes(' relay ') || c.includes('typ relay')) relay = true;
    };
    pc.createDataChannel('probe');
    pc.createOffer().then((o) => pc.setLocalDescription(o)).catch(() => resolve({ supported: false, ipv4: null, ipv6: null, natType: null }));
    setTimeout(() => {
      pc.close();
      const base = srflx ? 'srflx (likely behind NAT)' : 'open';
      resolve({ supported: true, ipv4, ipv6, natType: relay ? `${base} · TURN relay reachable` : base });
    }, 6000);
  });
}

export function NetworkTestPage() {
  usePageText();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [progress, setProgress] = useState<string>('');

  async function run() {
    setRunning(true);
    setResult(null);
    setProgress('Pinging endpoints…');
    const rttResults = [];
    for (const t of RTT_TARGETS) {
      const r = await probe(t.url);
      rttResults.push({ url: t.label, ms: r.ms, status: r.ok ? 'ok' as const : 'fail' as const });
    }

    setProgress('Measuring download throughput…');
    const dl = await measureDownload();

    setProgress('Measuring upload throughput…');
    const up = await measureUpload();

    setProgress('Checking codec support…');
    const codecs = CODECS_TO_TEST.map((c) => ({
      codec: c.replace(/^.*?codecs="/, '').replace(/"$/, '').split(';')[0],
      supported: !!document.createElement('video').canPlayType(c),
    }));

    setProgress('Probing WebRTC + NAT…');
    const webrtc = await discoverWebRtc(await getIceServers());

    setProgress('');
    setResult({ rtt: rttResults, download: dl, upload: up, codecs, webrtc });
    setRunning(false);
  }

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 28 }}>
        <div>
          <h1 className="h1">{tx("Network test")}</h1>
          <p className="subtitle">{tx("Run a one-shot probe of your network from this browser. Useful before your first stream.")}</p>
        </div>
        <button className="btn primary lg" onClick={run} disabled={running}>{running ? tx("Running…") : tx("Run test")}</button>
      </div>

      {running && progress && <div className="card muted" style={{ textAlign: 'center', padding: 20 }}>{progress}</div>}

      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h3 style={{ marginTop: 0 }}>{tx("Latency")}</h3>
            <table>
              <thead><tr><th>{tx("Target")}</th><th>{tx("RTT")}</th><th>{tx("Status")}</th></tr></thead>
              <tbody>
                {result.rtt.map((r) => (
                  <tr key={r.url}>
                    <td>{r.url}</td>
                    <td className="mono">{ptx("{v0} ms", {v0: (r.ms)})}</td>
                    <td><span className={`badge ${r.status === 'ok' ? 'success' : 'live'}`}>{r.status === 'ok' ? tx("reachable") : tx("unreachable")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>{tx("Throughput")}</h3>
            <div className="kpi-grid">
              <div className="kpi accent">
                <div className="label">{tx("Download")}</div>
                <div className="value">{result.download?.mbps.toFixed(2) ?? '—'}</div>
                <div className="sub">{tx("Mbps · ")}{result.download?.durationMs ?? '—'}{tx("ms")}</div>
              </div>
              <div className="kpi">
                <div className="label">{tx("Upload")}</div>
                <div className="value">{result.upload?.mbps.toFixed(2) ?? '—'}</div>
                <div className="sub">{tx("Mbps · ")}{result.upload?.durationMs ?? '—'}{tx("ms")}</div>
              </div>
              <div className="kpi">
                <div className="label">{tx("Recommended for")}</div>
                <div className="value" style={{ fontSize: 16 }}>
                  {(() => {
                    const u = result.upload?.mbps ?? 0;
                    if (u >= 8) return '1080p60 broadcast';
                    if (u >= 5) return '1080p30';
                    if (u >= 3) return '720p30';
                    if (u >= 1.5) return '480p30';
                    return 'audio-only';
                  })()}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>{tx("Codec support (this browser)")}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {result.codecs.map((c) => (
                <span key={c.codec} className={`badge ${c.supported ? 'success' : ''}`} style={{ opacity: c.supported ? 1 : 0.4 }}>
                  {c.codec} {c.supported ? '✓' : '✗'}
                </span>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>{tx("WebRTC + NAT")}</h3>
            <table>
              <tbody>
                <tr><th>{tx("WebRTC supported")}</th><td>{result.webrtc.supported ? tx("yes") : tx("no")}</td></tr>
                <tr><th>{tx("Public IPv4")}</th><td className="mono">{result.webrtc.ipv4 ?? '—'}</td></tr>
                <tr><th>{tx("Public IPv6")}</th><td className="mono">{result.webrtc.ipv6 ?? '—'}</td></tr>
                <tr><th>{tx("NAT type")}</th><td>{result.webrtc.natType ?? '—'}</td></tr>
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>{tx("If WebRTC reports \"srflx (likely behind NAT)\" you can still publish via WHIP, but ultra-low latency may need a TURN relay. Symmetric NAT environments will require TURN.")}</p>
          </div>
        </div>
      )}

      {!result && !running && (
        <div className="card muted" style={{ textAlign: 'center', padding: 60 }}>{tx("Click \"Run test\" above. The whole probe takes ~10 seconds and runs entirely in your browser — we don't store the results.")}</div>
      )}
    </div>
  );
}
